import * as YAML from 'js-yaml';

import { captureError } from 'shared/base';

import { buildYourOwnTemplate, templates } from '../templates';

import { Report } from '../types/report';
import { Template } from '../types/templates';

export function parseReportYaml(yaml: string): Report {
  try {
    const parsedYaml: { report: Report } = YAML.load(yaml) as unknown as {
      report: Report;
    };

    return parsedYaml.report;
  } catch (error) {
    captureError(`Platform reports yaml parsing error: ${error.message}`);

    return {} as Report;
  }
}

function convertQueriesToBlockStyle(obj: any): any {
  if (Array.isArray(obj)) {
    return obj.map((item) => convertQueriesToBlockStyle(item));
  }

  if (obj && typeof obj === 'object') {
    const newObj: any = {};
    for (const key of Object.keys(obj)) {
      if (key === 'query' && typeof obj[key] === 'string') {
        // Clean up and apply block scalar style
        const query = obj[key].trim().replace(/^\|/, '').trim();
        newObj[key] = query;
      } else {
        newObj[key] = convertQueriesToBlockStyle(obj[key]);
      }
    }

    return newObj;
  }

  return obj;
}

async function generateAndCompleteReportYaml(templateId: string, baseYaml: string) {
  const template = templates.find((template: Template) => template.id === templateId);
  const additionalObject = await template?.generator?.();
  if (!additionalObject) {
    return baseYaml;
  }

  const baseReportObject = parseReportYaml(baseYaml);
  const completeYamlObject = {
    report: {
      ...baseReportObject,
      ...convertQueriesToBlockStyle(additionalObject),
    },
  };

  return YAML.dump(completeYamlObject, { indent: 2, lineWidth: 160, noRefs: true });
}

export const generateReportObjectWithYamlFromTemplate = async (
  templateId?: string,
): Promise<{ content: string; description: string; title: string }> => {
  const template = templateId && templates.find((template: Template) => template.id === templateId);
  if (template) {
    const reportObject = parseReportYaml(template.yaml);

    if (reportObject && !reportObject?.groups && !reportObject?.widgets) {
      const completeYaml = await generateAndCompleteReportYaml(templateId!, template.yaml);

      return {
        content: completeYaml,
        description: reportObject.shortDescription ?? '',
        title: reportObject.title ?? '',
      };
    }

    return {
      content: template.yaml,
      description: reportObject.shortDescription ?? '',
      title: reportObject.title ?? '',
    };
  }

  // build from scratch
  const reportObject = parseReportYaml(buildYourOwnTemplate.yaml);

  return {
    content: buildYourOwnTemplate.yaml,
    description: reportObject?.shortDescription ?? '',
    title: reportObject?.title ?? '',
  };
};
